export default class Measurements {
	constructor(dimensionalMu?: string, weightMu?: string) {
		this.dimensionalMu = dimensionalMu
		this.weightMu = weightMu
		this.kgToLb = 2.2046226218487757
		this.inToCm = 2.54 // 0.39370078740157477
		this.mmToIn = 1 / (this.inToCm * 10)
		this.meterToMile = 0.0006213711922373339
	}

	init(order, authDealerId) {
		this.dimensionalMu = order?.dealer_dimensions_mu
		this.weightMu = order?.dealer_weight_mu
		if (authDealerId === 'syntheticIdForManagersCommonUsDealer') {
		    this.dimensionalMu = 'in'
		    this.weightMu = 'lb'
		} else if (authDealerId === 'syntheticIdForManagersCommonGbDealer') {
		    this.dimensionalMu = 'cm'
		    this.weightMu = 'kg'
		} else if (authDealerId === '35877652-9900-4767-b64b-8214dac5e848') { // 'VD Russia'
		    this.dimensionalMu = 'cm'
		    this.weightMu = 'kg'
		} else {
		    Object.keys(order?.dealerAndChildDealers || {}).some(dealerId => {
		      if (dealerId === authDealerId) {
		        const dealer = order.dealerAndChildDealers[dealerId]
		        if (dealer) {
		            this.dimensionalMu = dealer.dealer_dimensions_mu
		            this.weightMu = dealer.dealer_weight_mu
		        	return true
		        }
		      }
		    })
		}
	}

	public mmToMu(mm: number): number {
		let dimensionInMu = mm / 10
		if (this.dimensionalMu === 'in') {
			dimensionInMu = Math.round(mm * this.mmToIn * 100) / 100
 		}
		return dimensionInMu
	}

	public cmToMu(cm: number): number {
		return this.mmToMu(cm * 10)
	}

	public mToMu(m: number, roundType: string): number {
		let dimensionInMu = m
		if (this.dimensionalMu === 'in') {
			dimensionInMu = m * this.meterToMile
			if (roundType === 'floor1') {
				dimensionInMu = Math.floor(dimensionInMu * 10) / 10
			}
		}
		return dimensionInMu
	}

	public inToMu(value: number): number {
		let dimensionInMu = value
		if (this.dimensionalMu === 'cm') {
			dimensionInMu = Math.round((value / (this.mmToIn * 10)) * 100) / 100
 		}
		return dimensionInMu	
	}

	public muToMm(value: number): number {
		let dimensionInMm = value * 10
		if (this.dimensionalMu === 'in') {
			dimensionInMm = Math.round(value / this.mmToIn * 100) / 100
 		}
		return dimensionInMm
	}

	public muToM(mu: number, roundType: string): number {
		let dimensionInM = mu
		if (this.dimensionalMu === 'in') {
			dimensionInM = mu / this.meterToMile
			if (roundType === 'ceil') {
				dimensionInM = Math.ceil(dimensionInM)
			}
 		}
		return dimensionInM
	}

	public grToMu(gr: number): number {
		let weightInMu = gr / 1000
		if (this.weightMu === 'lb') {
			weightInMu = Math.round((gr / 1000) * this.kgToLb * 1000) / 1000
 		}
		return weightInMu
	}

	public grToOz(gr: number): number {
		const oz = Math.ceil((gr * (this.kgToLb / 1000)) * 16)
		return oz
	}

	public grToLb(gr: number): number {
		const lb = Math.ceil(gr * (this.kgToLb / 1000))
		return lb
	}

	public muToGr(value: number): number {
		let weightInGr = value * 1000
		if (this.weightMu === 'lb') {
			weightInGr = Math.round(((value / this.kgToLb) * 1000) * 1000) / 1000
 		}
		return weightInGr
	}

	public kgToMu(kg: number): number {
		return this.grToMu(kg * 1000)
	}

	public lbToMu(lb: number): number {
		let weightInMu = lb
		if (this.weightMu === 'kg') {
			weightInMu = Math.round(lb / this.kgToLb * 1000) / 1000
		}
		return weightInMu
	}

	public muToLb(value: number): number {
		if (this.weightMu === 'lb') {
			return value
		} else if (this.weightMu === 'oz') {
			return Math.round((value / 16) * 1000) / 1000
		}
	}

	public muToMu(value: number, externalMu: string, isWeight: boolean) {
		if (isWeight) {
			if (externalMu === this.weightMu) {
				return value
			} else if (externalMu === 'kg') {
				return this.kgToMu(value)
			} else if (externalMu === 'g') {
				return this.grToMu(value)
			} else if (externalMu === 'lb') {
				return this.lbToMu(value)
			} else if (this.weightMu === 'lb' && externalMu === 'oz') {
				return value
			}
		} else {
			if (externalMu === this.dimensionalMu) {
				return value
			} else if (externalMu === 'cm') {
				return this.cmToMu(value)
			} else if (externalMu === 'mm') {
				return this.cmToMu(value / 10)
			} else if (externalMu === 'm') {
				return this.cmToMu(value * 100)
			} else if (externalMu === 'in') {
				return this.inToMu(value)
			}
		}
	}

	public muToString(value: number, t, isWeight: boolean, externalMu: string) {
		if (isWeight) {
			if (this.weightMu === 'lb') {
				if (externalMu === 'oz') {
					return `${value + t('oz')}`
				} else {
					let lb = Math.trunc(value)
				    let oz = Math.ceil((value % 1) * 16)
				    if (oz === 16) {
				      lb += 1
				      oz = 0
				    }
				    if (lb || oz) {
						return `${lb ? lb + t('lb') : ''} ${oz > 0 ? oz + t('oz') : ''}`
					} else {
						return 0 + t(externalMu)
					}
				}
			} else {
				return Math.round(value * 1000) / 1000 + t('kg')
			}
		} else {
			return Math.round(value * 10) / 10 + t(this.dimensionalMu)
		}
	}

	public getByOrder(order) {
		let muLength = order.mu_in_length
		if (!muLength) {
			muLength = order.defaultPackageLengthMu || this.mmToMu(200)
		}
		let muWidth = order.mu_in_width
		if (!muWidth) {
			muWidth = order.defaultPackageWidthMu || this.mmToMu(200)
		}
		let muHeight = order.mu_in_height
		if (!muHeight) {
			muHeight = order.defaultPackageHeightMu || this.mmToMu(100)
		}
		let muWeight = order.mu_in_weight
		if (!muWeight) {
			muWeight = order.defaultPackageWeightMu || this.grToMu(100)
		}
		return { 
			muLength, muWidth, muHeight,
			dimensionalMu: order.mu_order_dim,
			muWeight,
			weightMu: order.mu_order_weight,
		}
	}
}